import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Spinner } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2, AlertTriangle, CircleDashed, Wrench, RefreshCw, ChevronDown,
  Gauge, ListChecks,
} from "lucide-react";
import { toast } from "sonner";

const ring = (pct, color, size = 132) => {
  const r = (size - 14) / 2;
  const c = 2 * Math.PI * r;
  const off = c * (1 - Math.max(0, Math.min(100, pct)) / 100);
  return (
    <svg width={size} height={size} className="-rotate-90" data-testid="completion-ring">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--border))" strokeWidth="10" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="10"
        strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
        style={{ transition: "stroke-dashoffset .9s cubic-bezier(.2,.8,.2,1)" }} />
    </svg>
  );
};

export default function PlatformCompletion() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/completion");
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the completion audit");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading && !data) return <Spinner />;
  if (!data) return null;

  const meta = data.status_meta;
  const overallColor = data.overall >= 85 ? "#22c55e" : data.overall >= 65 ? "#eab308" : "#f97316";

  return (
    <div className="space-y-6" data-testid="platform-completion">
      {/* Header / gauge */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="relative shrink-0 grid place-items-center" style={{ width: 132, height: 132 }}>
            {ring(data.overall, overallColor)}
            <div className="absolute inset-0 grid place-items-center">
              <div className="text-3xl font-extrabold" data-testid="completion-overall" style={{ color: overallColor }}>
                {data.overall}%
              </div>
              <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">complete</div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Gauge className="w-5 h-5 text-[hsl(var(--primary))]" />
              <h2 className="text-lg font-bold">Platform Completion Matrix</h2>
            </div>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">
              {data.feature_count} features across {data.category_count} systems · generated {new Date(data.generated_at).toLocaleString()}
            </p>
            <div className="flex flex-wrap gap-2">
              {data.status_order.map((k) => (
                <span key={k} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-[hsl(var(--border))]"
                  data-testid={`completion-total-${k}`}>
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: meta[k].color }} />
                  <span className="font-semibold">{data.status_totals[k] || 0}</span>
                  <span className="text-[hsl(var(--muted-foreground))]">{meta[k].label}</span>
                </span>
              ))}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="completion-refresh" className="shrink-0">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>
        {/* Live counts */}
        {data.live && Object.keys(data.live).length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-5 pt-5 border-t border-[hsl(var(--border))]">
            {[["Users", data.live.users], ["Games", data.live.games], ["Published", data.live.games_published],
              ["Plays", data.live.plays], ["QA runs", data.live.qa_reports], ["Posts", data.live.community_posts]].map(([l, v]) => (
              <div key={l} className="text-center">
                <div className="text-xl font-bold">{v ?? 0}</div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{l}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Category matrix */}
      <div className="grid md:grid-cols-2 gap-4">
        {data.matrix.map((cat) => {
          const isOpen = open[cat.category] !== false; // default open
          const cColor = cat.completion >= 85 ? "#22c55e" : cat.completion >= 60 ? "#eab308" : "#f97316";
          return (
            <div key={cat.category} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden"
              data-testid={`completion-cat-${cat.category.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
              <button onClick={() => setOpen((o) => ({ ...o, [cat.category]: !isOpen }))}
                className="w-full flex items-center gap-3 p-4 hover:bg-[hsl(var(--secondary))]/40 transition-colors text-left">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{cat.category}</div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-[hsl(var(--border))] overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${cat.completion}%`, background: cColor, transition: "width .8s" }} />
                  </div>
                </div>
                <span className="text-sm font-bold tabular-nums" style={{ color: cColor }}>{cat.completion}%</span>
                <ChevronDown className={`w-4 h-4 text-[hsl(var(--muted-foreground))] transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="px-4 pb-4 space-y-2">
                  {cat.features.map((f, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-sm">
                      <span className="w-2.5 h-2.5 rounded-full mt-1.5 shrink-0" style={{ background: meta[f.status].color }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{f.name}</span>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0" style={{ color: meta[f.status].color, borderColor: meta[f.status].color }}>
                            {meta[f.status].label}
                          </Badge>
                        </div>
                        {f.note && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{f.note}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Remediation backlog */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="completion-backlog">
        <div className="flex items-center gap-2 mb-4">
          <ListChecks className="w-5 h-5 text-[hsl(var(--primary))]" />
          <h3 className="font-bold">Prioritized remediation backlog</h3>
          <Badge variant="outline" className="ml-auto">{data.backlog.length} items</Badge>
        </div>
        {data.backlog.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-emerald-500">
            <CheckCircle2 className="w-4 h-4" /> Everything is complete — nothing to remediate. 🎉
          </div>
        ) : (
          <div className="space-y-2.5">
            {data.backlog.map((b, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/30"
                data-testid={`backlog-item-${i}`}>
                <span className="grid place-items-center w-6 h-6 rounded-full text-xs font-bold shrink-0 mt-0.5"
                  style={{ background: `${meta[b.status].color}22`, color: meta[b.status].color }}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{b.name}</span>
                    <span className="text-[11px] text-[hsl(var(--muted-foreground))]">· {b.category}</span>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0" style={{ color: meta[b.status].color, borderColor: meta[b.status].color }}>
                      {meta[b.status].label}
                    </Badge>
                  </div>
                  {b.fix && (
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1 flex items-start gap-1.5">
                      <Wrench className="w-3 h-3 mt-0.5 shrink-0" /> {b.fix}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
